// SignInOverlay — sign-in panel rendered as an in-shell cover (Task 13, D3).
//
// Wraps SignInPanel (Task 1) — does not duplicate its OTP logic — inside an
// opaque band that reads as the only thing in the route-content area. Uses
// only existing theme tokens (no scrim/backdrop token exists yet, and adding
// one is out of scope for the task that introduced this component):
// `colors.surface` as an OPAQUE background (simpler and higher-contrast than
// a translucent scrim, and avoids inventing a token) with the panel itself
// styled as a `cardBg`/`cardBorder`/`cardShadow` card.
//
// It "covers" by REPLACEMENT, not by overlay: the caller takes the route
// content out of layout (`display: none`) and marks it `inert` while gated,
// so there is nothing left to paint over. That is what lets this component
// live in normal flow — see the style block below for why that matters.
//
// Accessibility: role="dialog" + aria-label="Sign in" — deliberately NOT
// aria-modal. This overlay exists specifically so the storefront toolbar
// (StorefrontShell, a sibling outside this component's own subtree) stays
// reachable while it is showing — that survival is spec defect D3, the
// entire point of the task this component was built for. aria-modal="true"
// instructs assistive-technology implementations to restrict the EXPOSED
// accessibility tree to the dialog subtree, which would hide that toolbar
// from AT users — silently reproducing the exact "stranded" outcome for the
// population least able to recover from it, while sighted/mouse users keep
// full access. It would also create two disagreeing models: aria-modal
// claims nothing exists outside the dialog, but there is no focus trap here,
// so a keyboard user CAN tab out into toolbar content AT has been told does
// not exist. The property that actually matters — the covered ROUTE CONTENT
// must not be interactable — is provided by the caller marking that content
// `inert`, not by this component claiming modality over content it
// deliberately does not cover.
//
// Focus moves to the dialog container on mount (not into SignInPanel's
// fields — it has no autofocus on its email step) so keyboard/AT users land
// on the sign-in form immediately rather than on a stale, now-inert target.

import { useEffect, useRef } from "react";
import { useEmbedTokens, EmbedButton } from "../../embed-shared/primitives";
import { SignInPanel } from "./SignInPanel";

export interface SignInOverlayProps {
  onSignIn: (jwt: string, refreshToken?: string) => void;
  recaptchaSiteKey?: string;
  /** Runtime mount eligibility for passkeys (RULING R-M) — threaded straight
   *  through to SignInPanel. See that file's own prop doc for the full
   *  rationale (never inline this at build time). */
  passkeyEligible?: boolean;
  /** Optional dismiss callback — renders a "Not now" exit button when supplied. */
  onDismiss?: () => void;
  /**
   * True while SignInPanel is showing the post-signup passkey-enrollment
   * step (Task 11) — the customer is ALREADY authenticated at that point, so
   * this overlay's OWN "Not now" dismiss button is suppressed while it's
   * active. Without this, a customer could dismiss THIS overlay mid-offer,
   * leaving MarketplaceRouter's pendingEnrollOffer state stuck true (nothing
   * would ever clear it) on top of showing two confusingly-duplicate
   * "Not now" controls at once.
   */
  enrollPromptActive?: boolean;
  /** Forwarded straight through to SignInPanel — see that file's
   *  finishOtpSignIn for what drives it. */
  onEnrollPromptActiveChange?: (active: boolean) => void;
}

export function SignInOverlay({
  onSignIn,
  recaptchaSiteKey,
  passkeyEligible,
  onDismiss,
  enrollPromptActive,
  onEnrollPromptActiveChange,
}: SignInOverlayProps) {
  const t = useEmbedTokens();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-label="Sign in"
      tabIndex={-1}
      style={{
        // IN FLOW — deliberately not `position: absolute; inset: 0`. An
        // out-of-flow element contributes no height to its parent, so the
        // wrapper had to reserve a guessed one (360px), and the panel then
        // scrolled inside it via overflowY:auto instead of the box growing.
        // Measured 2026-09-05 on www.aldelo.com/marketplace: 88px short at
        // 1440px, 119px short at 390px, the overflow painting over the store
        // footer (TD-452). In flow, the wrapper — and with it the mount div
        // and the Framer Fit Content instance above it — grows to the panel,
        // so no reservation is needed and future content growth is absorbed.
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: t.spacing.xl,
        paddingBottom: t.spacing.xl,
        backgroundColor: t.colors.surface,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          backgroundColor: t.colors.cardBg,
          border: `1px solid ${t.colors.cardBorder}`,
          borderRadius: t.borderRadiusLg,
          boxShadow: t.colors.cardShadow,
        }}
      >
        <SignInPanel
          onSignIn={onSignIn}
          recaptchaSiteKey={recaptchaSiteKey}
          passkeyEligible={passkeyEligible}
          onEnrollPromptActiveChange={onEnrollPromptActiveChange}
        />
        {onDismiss && !enrollPromptActive && (
          <div style={{ padding: `0 ${t.spacing.lg}px ${t.spacing.lg}px`, textAlign: "center" }}>
            <EmbedButton type="button" variant="ghost" size="sm" onClick={onDismiss}>
              Not now
            </EmbedButton>
          </div>
        )}
      </div>
    </div>
  );
}
